import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Database, RefreshCw, BarChart3, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import KnowledgeEmbedderButton from '@/components/chippie/KnowledgeEmbedderButton';

interface DataSourceStats {
  source_table: string;
  count: number;
  lastUpdated?: string;
}

interface EmbeddingProgress {
  isRunning: boolean;
  currentSource?: string;
  processedCount: number;
  totalCount: number;
  startTime?: Date;
}

const KnowledgeBaseManagement: React.FC = () => {
  const [dataSourceStats, setDataSourceStats] = useState<DataSourceStats[]>([]);
  const [totalEmbeddings, setTotalEmbeddings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<EmbeddingProgress>({
    isRunning: false,
    processedCount: 0,
    totalCount: 0
  });

  const dataSourceLabels: Record<string, string> = {
    farms: 'Farms',
    agents: 'Agents',
    suppliers: 'Suppliers',
    agent_products: 'Agent Products',
    chipchip_sales_data: 'ChipChip Sales Data',
    local_shop_prices: 'Local Shop Prices',
    supermarket_prices: 'Supermarket Prices',
    ecommerce_prices: 'Ecommerce Prices',
    distribution_center_prices: 'Distribution Center Prices',
    sunday_market_prices: 'Sunday Market Prices',
    farm_prices: 'Farm Prices',
    harvests: 'Harvests',
    farm_products: 'Farm Products',
    products: 'Products',
    procurements: 'Procurements',
    procurement_items: 'Procurement Items',
    procurement_warehouse: 'Procurement Warehouse',
    procurement_finance: 'Procurement Finance'
  };

  const getDataSourceCategory = (source: string) => {
    if (['chipchip_sales_data', 'local_shop_prices', 'supermarket_prices', 'ecommerce_prices', 'distribution_center_prices', 'sunday_market_prices', 'farm_prices'].includes(source)) {
      return 'Price Intelligence';
    }
    if (['procurements', 'procurement_items', 'procurement_warehouse', 'procurement_finance', 'harvests'].includes(source)) {
      return 'Supply Chain & Operations';
    }
    return 'Network & Product Data';
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Price Intelligence':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Supply Chain & Operations':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-purple-100 text-purple-800 border-purple-200';
    }
  };

  const fetchKnowledgeBaseStats = async () => {
    setLoading(true);
    try {
      const { data, error } = await (supabase
        .from('knowledge_embeddings' as any)
        .select('source_table, created_at')
        .order('created_at', { ascending: false })) as { data: { source_table: string; created_at: string }[] | null; error: any };

      if (error) throw error;

      // Group by source table and count
      const statsMap = new Map<string, { count: number; lastUpdated: string }>();
      
      data?.forEach((item) => {
        const existing = statsMap.get(item.source_table);
        if (existing) {
          existing.count++;
          if (item.created_at > existing.lastUpdated) {
            existing.lastUpdated = item.created_at;
          }
        } else {
          statsMap.set(item.source_table, {
            count: 1,
            lastUpdated: item.created_at
          });
        }
      });

      const stats: DataSourceStats[] = Array.from(statsMap.entries()).map(([source_table, data]) => ({
        source_table,
        count: data.count,
        lastUpdated: data.lastUpdated
      })).sort((a, b) => b.count - a.count);

      setDataSourceStats(stats);
      setTotalEmbeddings(data?.length || 0);
    } catch (error) {
      console.error('Error fetching knowledge base stats:', error);
      toast.error('Failed to fetch knowledge base statistics');
    } finally {
      setLoading(false);
    }
  };

  const groupedStats = dataSourceStats.reduce((acc, stat) => {
    const category = getDataSourceCategory(stat.source_table);
    if (!acc[category]) acc[category] = [];
    acc[category].push(stat);
    return acc;
  }, {} as Record<string, DataSourceStats[]>);

  useEffect(() => {
    fetchKnowledgeBaseStats();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading knowledge base statistics...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Knowledge Base Management</h1>
            <p className="text-muted-foreground">
              Manage and monitor ChippieAI's knowledge base embeddings
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchKnowledgeBaseStats}
              disabled={loading}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <KnowledgeEmbedderButton />
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Embeddings</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalEmbeddings.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Across {dataSourceStats.length} data sources
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Data Sources</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dataSourceStats.length}</div>
              <p className="text-xs text-muted-foreground">
                Active knowledge sources
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Status</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">Active</div>
              <p className="text-xs text-muted-foreground">
                Knowledge base ready
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Data Sources Breakdown */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="price-intelligence">Price Intelligence</TabsTrigger>
            <TabsTrigger value="supply-chain">Supply Chain</TabsTrigger>
            <TabsTrigger value="network-product">Network & Products</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Knowledge Base Distribution</CardTitle>
                <CardDescription>
                  Breakdown of embeddings by data source category
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(groupedStats).map(([category, stats]) => {
                    const categoryTotal = stats.reduce((sum, stat) => sum + stat.count, 0);
                    const percentage = totalEmbeddings > 0 ? (categoryTotal / totalEmbeddings) * 100 : 0;
                    
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={getCategoryColor(category)}>
                            {category}
                          </Badge>
                          <span className="text-sm font-medium">
                            {categoryTotal.toLocaleString()} ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {Object.entries(groupedStats).map(([category, stats]) => {
            const tabValue = category.toLowerCase().replace(/\s+/g, '-').replace('&', '');
            return (
            <TabsContent 
              key={tabValue} 
              value={tabValue}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.map((stat) => (
                  <Card key={stat.source_table}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">
                        {dataSourceLabels[stat.source_table] || stat.source_table}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold mb-2">
                        {stat.count.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Last updated: {stat.lastUpdated ? 
                          new Date(stat.lastUpdated).toLocaleDateString() : 
                          'Unknown'
                        }
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </Layout>
  );
};

export default KnowledgeBaseManagement;